import React, { useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { shuffleQuestionOptions } from '../utils/shuffleOptions';

const Card = styled(motion.div)`
  background-color: rgba(18, 18, 28, 0.82);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 650px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--primary-color);
  }

  @media (min-width: 640px) {
    padding: 2.25rem;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--text-secondary);
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
`;

const QuestionNumber = styled.div`
  font-size: 0.95rem;
  color: var(--text-secondary);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ProgressBar = styled.div`
  height: 6px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  width: 100%;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
`;

const ProgressText = styled.span`
  margin-left: 0.5rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  font-weight: 600;
`;

const Progress = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: var(--primary-color);
  width: ${props => props.progress || '0%'};
  transition: width 0.35s ease;
  border-radius: 999px;
`;

const QuestionText = styled.h2`
  font-family: var(--font-display);
  font-size: 1.4rem;
  color: var(--text-primary);
  margin-bottom: 1.35rem;
  line-height: 1.25;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (min-width: 640px) {
    font-size: 1.75rem;
    margin-bottom: 1.75rem;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media (min-width: 640px) {
    gap: 0.85rem;
  }
`;

const OptionButton = styled(motion.button)`
  background-color: rgba(255, 255, 255, 0.03);
  padding: 0.9rem 1rem;
  border-radius: 12px;
  color: var(--text-primary);
  text-align: left;
  font-size: 0.98rem;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.selected ? 'var(--primary-light)' : 'rgba(255, 255, 255, 0.1)'};
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  font-weight: 500;

  &:hover {
    background-color: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.18);
  }

  &:focus-visible {
    outline: 2px solid rgba(var(--primary-rgb), 0.65);
    outline-offset: 3px;
  }

  @media (min-width: 640px) {
    padding: 1.1rem 1.15rem;
    font-size: 1.05rem;
  }
`;

const ButtonHighlight = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: var(--primary-color);
  opacity: ${props => props.selected ? 1 : 0};
  pointer-events: none;
`;

const OptionText = styled.span`
  position: relative;
  z-index: 1;
  line-height: 1.45;
`;

const OptionLetter = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 8px;
  background-color: ${props => props.selected ? 'rgba(var(--primary-rgb), 0.22)' : 'rgba(255, 255, 255, 0.08)'};
  color: var(--text-primary);
  margin-right: 12px;
  font-weight: 700;
  font-size: 0.85rem;
  position: relative;
  z-index: 1;
`;

const Hint = styled.p`
  margin-top: 1.25rem;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
`;

const QuestionCard = ({ 
  question, 
  currentIndex, 
  totalQuestions, 
  selectedOption, 
  setSelectedOption, 
  onNext,
  onBack,
  autoAdvance = true,
}) => {
  const advanceTimerRef = useRef(null);
  const progress = ((currentIndex + 1) / totalQuestions) * 100;
  const displayOptions = useMemo(
    () => shuffleQuestionOptions(question),
    [question]
  );

  useEffect(() => () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    if (!autoAdvance) return;

    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = setTimeout(() => {
      onNext(option);
    }, 420);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
    exit: { opacity: 0, y: -16, transition: { duration: 0.25 } },
  };

  return (
    <Card variants={cardVariants} initial="hidden" animate="visible" exit="exit">
      <TopRow>
        <BackButton type="button" onClick={onBack} aria-label="Go back">
          ← Back
        </BackButton>
        <QuestionNumber>
          {currentIndex + 1} / {totalQuestions}
          <ProgressText>{Math.round(progress)}%</ProgressText>
        </QuestionNumber>
      </TopRow>

      <ProgressBar aria-hidden="true">
        <Progress progress={`${progress}%`} />
      </ProgressBar>
      
      <QuestionText>{question.text}</QuestionText>
      
      <OptionsContainer>
        {displayOptions.map(option => (
          <OptionButton
            key={option.id}
            selected={selectedOption?.id === option.id}
            onClick={() => handleOptionClick(option)}
            whileTap={{ scale: 0.99 }}
            type="button"
          >
            <ButtonHighlight selected={selectedOption?.id === option.id} />
            <OptionLetter selected={selectedOption?.id === option.id}>
              {option.id.toUpperCase()}
            </OptionLetter>
            <OptionText>{option.text}</OptionText>
          </OptionButton>
        ))}
      </OptionsContainer>

      <Hint>Tap an answer to continue</Hint>
    </Card>
  );
};

export default QuestionCard;
